import { atomFamily, atomWithReducer } from 'jotai/utils';
import { match } from 'ts-pattern';
import type { AIMessage } from '~/common/vm/protocol';

type AILogsState = {
  testContentId: string;
  logs: AIMessage[][];
  foregroundIndex: number;
};

type AILogsAction =
  | { type: 'startNewLog' }
  | { type: 'appendMessageToCurrentLog'; message: AIMessage }
  | { type: 'navigatePrev' }
  | { type: 'navigateNext' }
  | { type: 'navigateLast' };

// AI chat logs reducer
const aiLogsReducer = (state: AILogsState, action: AILogsAction) =>
  match(action)
    .with({ type: 'startNewLog' }, () => {
      // If we only have the initial empty log, reuse it instead of adding a new one.
      const lastLogIndex = state.logs.length - 1;
      if (state.logs.length > 0 && state.logs[lastLogIndex].length === 0) {
        return { ...state, foregroundIndex: lastLogIndex };
      }
      return {
        ...state,
        logs: [...state.logs, []],
        foregroundIndex: lastLogIndex + 1,
      };
    })
    .with({ type: 'appendMessageToCurrentLog' }, ({ message }) => {
      const newLogs = [...state.logs];
      newLogs[state.logs.length - 1] = [...newLogs[state.logs.length - 1], message];
      return { ...state, logs: newLogs };
    })
    .with({ type: 'navigatePrev' }, () => {
      if (state.foregroundIndex > 0) {
        return { ...state, foregroundIndex: state.foregroundIndex - 1 };
      }
      return state;
    })
    .with({ type: 'navigateNext' }, () => {
      if (state.foregroundIndex < state.logs.length - 1) {
        return { ...state, foregroundIndex: state.foregroundIndex + 1 };
      }
      return state;
    })
    .with({ type: 'navigateLast' }, () => {
      const lastIndex = state.logs.length - 1;
      if (lastIndex >= 0 && state.foregroundIndex !== lastIndex) {
        return { ...state, foregroundIndex: lastIndex };
      }
      return state;
    })
    .exhaustive();

// AI chat logs atom family. Each testContentId has its own logs to avoid conflicts, each preview would create a new log.
export const aiChatLogsAtomFamily = atomFamily((testContentId: string) => {
  const initialState: AILogsState = {
    testContentId,
    logs: [[]],
    foregroundIndex: 0,
  };

  return atomWithReducer(initialState, aiLogsReducer);
});
